import type { GiftProduct, StandardCouponDays } from '../../data/giftProducts';
import { GIFT_PRODUCTS, isStandardCouponDays } from '../../data/giftProducts';
import type { Product } from '../../types';
import { transLn } from '../transLn';

export function formatGiftPriceKrw(price: number): string {
  if (!Number.isFinite(price)) return '';
  return `₩${Math.round(price).toLocaleString('ko-KR')}`;
}

export type GiftOrderPricing = {
  originalPrice: number;
  salePrice: number;
  discountPercent: number;
};

/** 표준 선물권 정가·판매가·할인율 (API와 무관하게 표시·결제에 사용) */
const GIFT_STANDARD_ORDER_PRICING: Record<
  StandardCouponDays,
  { listPriceKrw: number; salePriceKrw: number; discountPercent: number }
> = {
  100: { listPriceKrw: 21_600, salePriceKrw: 19_000, discountPercent: 12 },
  365: { listPriceKrw: 112_000, salePriceKrw: 56_000, discountPercent: 50 },
  1000: { listPriceKrw: 330_000, salePriceKrw: 132_000, discountPercent: 60 },
};

export function resolveGiftSalePriceKrw(product: { duration: number; price: number }): number {
  if (!isStandardCouponDays(product.duration)) return Math.round(product.price);
  return GIFT_STANDARD_ORDER_PRICING[product.duration].salePriceKrw;
}

export function getGiftStandardOrderPricing(product: {
  duration: number;
  price: number;
}): GiftOrderPricing | null {
  if (!isStandardCouponDays(product.duration)) return null;

  const { listPriceKrw, salePriceKrw, discountPercent } = GIFT_STANDARD_ORDER_PRICING[product.duration];
  return {
    originalPrice: listPriceKrw,
    salePrice: salePriceKrw,
    discountPercent,
  };
}

/** GET /product/list?isGiftProduct=true → Draft 선물 카드 표시용 */
export function giftProductFromApiProduct(product: Product): GiftProduct {
  const days = product.duration;
  const priceLabel = formatGiftPriceKrw(resolveGiftSalePriceKrw(product));

  if (isStandardCouponDays(days)) {
    const preset = GIFT_PRODUCTS.find((p) => p.days === days);
    if (preset) {
      return {
        ...preset,
        priceLabel,
      };
    }
  }

  return {
    id: 'custom',
    days,
    title: transLn(product.name),
    description: transLn(product.description),
    priceLabel,
    isCustom: true,
  };
}

export function sortGiftApiProducts(products: Product[]): Product[] {
  return [...products].sort((a, b) => a.duration - b.duration);
}
